import { Component, inject, Input, OnInit, SimpleChanges, OnDestroy } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Subject, combineLatest, startWith, takeUntil } from 'rxjs';
import { GetDictionaryDTO } from 'src/app/Common/common.interface';
import { UserLoginService } from 'src/app/Common/UserLoginService/userlogin.service';
import { ValueListService } from 'src/app/Common/ValueListService/value-list.service';
import { KeyValueRequestDTO } from 'src/app/Common/ValueListService/valuelist.interface';

@Component({
  selector: 'app-uic-tax',
  templateUrl: './uic-tax.component.html',
  styleUrls: ['./uic-tax.component.css']
})
export class UicTaxComponent implements OnInit, OnDestroy {
  private user = inject(UserLoginService).User();
  private valueListService = inject(ValueListService);

  @Input() FormGroup!: FormGroup;
  @Input() dependentOn!: string;

  taxDropdownList: GetDictionaryDTO[] = [];
  taxType: number = 0;
  taxValue: number = 0;
  totalAmount: number = 0;

  private destroy$ = new Subject<void>();

  ngOnInit(): void {
    // if(this.taxDropdownList.length <= 0) {
    //   this.getTaxTypeList();
    // }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['FormGroup'] && changes['FormGroup'].currentValue) {
      // Wait for taxDropdownList to be populated before initializing
      if (this.taxDropdownList.length > 0) {
        this.initializeComponent();
      } else {
        // Defer initialization until taxDropdownList is ready
        this.valueListService.ValueListItem_ReadKeyValueDictionaryByCode({
          dCode: 'Tax',
          tenantId: this.user.companyId,
          tenantBranchId: this.user.loggedInBranchId
        }).subscribe((res) => {
          this.taxDropdownList = res.items;
          this.initializeComponent();
        });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeComponent(): void {
    // Unsubscribe from previous subscriptions if any
    this.destroy$.next();

    // Initialize tax values — prefer gstrate (direct rate) over reverse-engineering from tax amount
    const gstRateKey = Object.keys(this.FormGroup.controls).find(
      key => key.trim().toLowerCase() === 'gstrate'
    );
    const gstRateValue = gstRateKey ? Number(this.FormGroup.get(gstRateKey)?.value || 0) : 0;

    if (gstRateValue > 0) {
      // gstrate is known — set tax type/value directly and recalculate
      const matchedItem = this.taxDropdownList.find(item => Number(item.key) === gstRateValue);
      this.taxType = matchedItem ? Number(matchedItem.key) : gstRateValue;
      this.taxValue = this.taxType;
      this.calculateTax();
    } else if (this.FormGroup.get('tax')?.value && this.FormGroup.get('tax')?.value !== 0) {
      this.reverseEngineerTax();
    } else {
      this.taxType = 0;
      this.taxValue = 0;
      this.calculateTax();
    }

    // Build the list of observables to watch: dependentOn, discount, and unitPrice (if distinct)
    const priceKeys = ['unitprice', 'price'];

    const unitPriceKey = Object.keys(this.FormGroup.controls).find(
      key => priceKeys.includes(key.trim().toLowerCase())
    );
    const watchFields = [this.dependentOn, 'discount'];
    if (unitPriceKey && unitPriceKey !== this.dependentOn) {
      watchFields.push(unitPriceKey);
    }

    const watchObservables = watchFields
      .map(field => this.FormGroup.get(field)?.valueChanges.pipe(startWith(this.FormGroup.get(field)?.value)))
      .filter((obs): obs is NonNullable<typeof obs> => !!obs);

    if (watchObservables.length > 0) {
      combineLatest(watchObservables).pipe(
        takeUntil(this.destroy$)
      ).subscribe(() => {
        this.calculateTax();
      });
    }

    // Subscribe to tax value changes to detect manual updates
    this.FormGroup.get('tax')?.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {

      if (this.taxDropdownList.length > 0) {
        this.reverseEngineerTax();
      }
    });

    // Subscribe to gstrate changes — when patched externally (e.g. item selection),
    // sync the dropdown and recalculate tax
    if (gstRateKey) {
      this.FormGroup.get(gstRateKey)?.valueChanges.pipe(
        takeUntil(this.destroy$)
      ).subscribe((rate: any) => {
        const rateNum = Number(rate || 0);
        const matchedItem = this.taxDropdownList.find(item => Number(item.key) === rateNum);
        this.taxType = matchedItem ? Number(matchedItem.key) : rateNum;
        this.taxValue = this.taxType;
        this.calculateTax();
      });
    }
  }

  // private getTaxTypeList(): void {
  //   const data: KeyValueRequestDTO = {
  //     dCode: 'Tax',
  //     loggedInCompanyId: this.user.companyId
  //   };

  //   this.valueListService.ValueListItem_ReadKeyValueDictionaryByCode(data)
  //     .pipe(takeUntil(this.destroy$))
  //     .subscribe((res) => {
  //       this.taxDropdownList = res.items;
  //       // If FormGroup is already set, initialize the component
  //       if (this.FormGroup) {
  //         console.log("getTaxTypeList()");
  //         this.initializeComponent();
  //       }
  //     });
  // }

  private reverseEngineerTax(): void {
    const taxAmount = Number(this.FormGroup.get('tax')?.value || 0);
    const price = Number(this.FormGroup.get(this.dependentOn)?.value || 0);
    const discount = Number(this.FormGroup.get('discount')?.value || 0);
    const total = Number(this.FormGroup.get('totalAmount')?.value || 0);

    if (!price || !taxAmount) {
      this.taxType = 0;
      this.taxValue = 0;
      this.calculateTax();
      return;
    }

    const taxableAmount = price - discount;

    if (!taxableAmount || taxableAmount <= 0) {
      this.taxType = 0;
      this.taxValue = 0;
      this.calculateTax();
      return;
    }

    const calculatedPercentage = Math.round((taxAmount / taxableAmount) * 100);
    const matchedItem = this.taxDropdownList.find(item => Number(item.key) === calculatedPercentage);
    const matchedKey = matchedItem ? Number(matchedItem.key) : 0;

    this.taxValue = matchedKey;
    this.taxType = matchedKey;
    this.totalAmount = total;

    // Recalculate to ensure consistency
    this.calculateTax();
  }

  updateTaxPerc(): void {
    this.taxValue = this.taxType;
    this.calculateTax();
  }

  /**
   * Rounds a rupee amount to 2 decimals (paise) exactly the way SQL Server stores a
   * DECIMAL(18,2) value: round-half-away-from-zero, computed on integer paise so that
   * floating-point artefacts (e.g. 190.225 -> 190.22499999) don't drop a paisa.
   */
  private roundToPaise(amountInRupees: number): number {
    return Math.round((amountInRupees + Number.EPSILON) * 100) / 100;
  }

  /**
   * Looks up a control name case-insensitively (UIC config may emit different casings).
   */
  private findControlKey(...candidates: string[]): string | undefined {
    const wanted = candidates.map(c => c.toLowerCase());
    return Object.keys(this.FormGroup.controls).find(
      key => wanted.includes(key.trim().toLowerCase())
    );
  }

  calculateTax(): void {

    this.FormGroup.get('GST')?.patchValue(0, { emitEvent: false });

    // --- Keep GSTRate in sync with the selected tax % --------------------------------
    const gstRateKey = this.findControlKey('gstrate');
    if (gstRateKey) {
      this.FormGroup.get(gstRateKey)?.patchValue(this.taxValue, { emitEvent: false });
    } else {
      this.FormGroup.addControl('gstRate', new FormControl(this.taxValue));
    }

    // ---------------------------------------------------------------------------------
    // Mirror crm.QuotationDetail_Update / erp.SalesDetail_Update tax logic EXACTLY so
    // the form's Tax Amt / Final Price match the values the stored procedure saves
    // (shown in the list below). The SP:
    //   1. TaxableAmount = (UnitPrice * Qty) - Discount        -> DECIMAL(18,2)
    //   2. INTRA-state: CGST = SGST = round(Taxable * Rate/2 / 100); Tax = CGST + SGST
    //      INTER-state: IGST = round(Taxable * Rate / 100);          Tax = IGST
    //   3. Total = Taxable + Tax
    // Each component is rounded to 2 decimals INDEPENDENTLY, which is why a single
    // Taxable*Rate/100 calculation can differ from the SP by one paisa. This component has
    // no access to either party's GSTIN, so it can't tell INTRA from INTER — defaulting to
    // the INTRA (CGST+SGST) formula since that's the common case; inter-state new entries
    // will be a paisa off until the backend SP (where the state comparison actually happens)
    // recomputes and self-corrects on save.
    //
    // The form's `discount` / `tax` controls are per-unit (the parent multiplies them by
    // quantity on submit), so taxable amount here is computed per-unit and the displayed
    // Final Price is scaled by quantity at the end — matching the SP for any quantity.
    // ---------------------------------------------------------------------------------
    const price = Number(this.FormGroup.get(this.dependentOn)?.value || 0);
    const discount = Number(this.FormGroup.get('discount')?.value || 0);
    const taxPercentage = Number(this.taxValue || 0);

    // DECIMAL(18,2) on the taxable amount, exactly as the SP column.
    const taxableAmount = this.roundToPaise(price - discount);
    const taxablePaise = Math.round(taxableAmount * 100);

    // CGST = SGST = round(Taxable * (Rate/2) / 100); in paise: round(taxablePaise * Rate / 200)
    const cgstAmt = Math.round((taxablePaise * taxPercentage) / 200) / 100;
    const taxAmount = this.roundToPaise(cgstAmt * 2);
    const totalAmt = this.roundToPaise(taxableAmount + taxAmount);

    // Patch with emitEvent:false so our own write doesn't re-trigger reverseEngineerTax().
    if (this.FormGroup.get('tax')?.value !== taxAmount) {
      this.FormGroup.get('tax')?.patchValue(taxAmount, { emitEvent: false });
    }

    
    this.totalAmount = totalAmt;

    // Patch totalAmount directly because tax is patched with emitEvent:false,
    // which prevents the parent valueChanges subscription from firing.
    // Look up the qty field by name (may be 'quantity' or 'qty' depending on UIC config).
    const qtyKey = this.findControlKey('quantity', 'qty');
    const quantity = Number((qtyKey ? this.FormGroup.get(qtyKey)?.value : null) || 1);
    const totalAmtKey = this.findControlKey('totalAmount', 'finalAmount');
    if (totalAmtKey) {
      this.FormGroup.get(totalAmtKey)?.patchValue(this.roundToPaise(totalAmt * quantity), { emitEvent: false });
    }
  }

  getFormControl(controlName: string): FormControl {
    return this.FormGroup.get(controlName) as FormControl;
  }
}